<template>
  <div id="contract-editor">
    <h1>
      <template v-if="!review">
        <span v-if="!renew">{{ $t(contractId ? 'EDIT_CONTRACT' : 'CREATE_CONTRACT') }}</span>
        <span v-if="!!renew">{{ $t('RENEW_CONTRACT') }}</span>
      </template>
      <template v-else>
        <span>{{ $t('CONTRACT') }}</span>
      </template>
      &nbsp;
      <span v-if="contractId">{{ `#${contractId}` }}</span>
    </h1>

    <div v-show="step === 0">
      <client-form v-model="client" ref="clientForm" :class="{ locked: !!renew }" />
      <asset-form v-model="asset" ref="assetForm" />
      <loan-form :asset="asset" :locked="true" ref="formerContractForm" v-if="!!renew" class="locked" />
      <loan-form v-model="contract" @transactions="emitTransactions" :asset="asset" :former-contract="formerContract" :review="review" @renewalTransactionsUpdated="loadRenewalInfo" ref="contractForm" />

      <div class="controls">
        <primary-button :text="$t('BACK')" blue class="prev" @click="back()" />
        <primary-button :text="$t('NEXT')" class="next" @click="showPreview()" />
      </div>
    </div>

    <div v-show="step === 1">
      <preview :client="client" :asset="asset" :contract="contract" :former-contract="formerContract" :review="review" ref="previewRef" :allTransactions="allTransactions.value" />
      <div class=" controls preview-controls">
        <center>{{ $t('PLEASE_CHECK_CONTRACT_INFO_BEFORE_PRINT') }}</center>

        <template v-if="!review">
          <div class="buttons d-flex justify-content-center">
            <primary-button v-if="substep === 0" :text="$t('BACK')" blue class="prev" @click="showInputs()" />
            <primary-button :text="$t((substep === 1) ? 'PRINT_CONTRACT' : 'SAVE_AND_PRINT_CONTRACT')" :blue="substep === 1" :class="{ next: substep === 1, prev: substep === 0 }" @click="saveContractAndPrint()">
              <template slot="icon">
                <font-awesome-icon :icon="faPrint" />
              </template>
            </primary-button>
            <primary-button v-if="substep === 1" :text="$t('DONE')" class="next" @click="complete()" />
          </div>

          <div class="checkbox-wrapper" v-if="renew && (substep === 1)">
            <label class="label">{{ $t('SEND_ZALO_NOTIFICATION') }}</label>
            <input type="checkbox" :checked="toSendZaloNotification" @change="toggleToSendZaloNotification()" />
          </div>
        </template>
        <template v-else>
          <div class="buttons d-flex justify-content-center">
            <primary-button :text="$t('BACK')" class="next" blue @click="goToContractDetail()" />
            <primary-button :text="$t('PRINT_CONTRACT')" class="prev" blue @click="print()">
              <template slot="icon">
                <font-awesome-icon :icon="faPrint" />
              </template>
            </primary-button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from '@vue/composition-api';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faPrint } from '@fortawesome/free-solid-svg-icons';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import moment from 'moment';

import { ORDER_STAGE, ORDER_STATUS, ORDER_STEP, PATHS, DISPLAY_DATE_FORMAT, DATE_FORMAT, CONTRACT_STATUSES, TRANSACTION_TYPES, TRANSACTION_ACTIONS } from '@/consts';
import { applyParams } from '@/helpers';

import PrimaryButton from '@/components/PrimaryButton';

import OrderService from '@/views/Order/OrderManagementService';

import ClientForm from './ClientForm';
import AssetForm from './AssetForm';
import LoanForm from './LoanForm';
import Preview from './Preview';

import ClientFormService from './ClientForm/ClientFormService';
import AssetFormService from './AssetForm/AssetFormService';
import LoanFormService from './LoanForm/LoanFormService';

const { useActions: useFlashActions } = createNamespacedHelpers('flash');

export default {
  name: 'ContractEditor',
  components: {
    ClientForm,
    AssetForm,
    LoanForm,
    Preview,
    PrimaryButton,
    FontAwesomeIcon
  },
  metaInfo() {
    const { $i18n } = this;
    const contractId = this.$route.params.id;

    let title = $i18n.t(contractId ? 'EDIT_CONTRACT' : 'CREATE_CONTRACT');
    if (this.$route.name === 'contract-renewal') {
      title = $i18n.t('RENEW_CONTRACT');
    }

    return {
      title
    };
  },
  props: {
    review: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { root }) {
    const { info, error } = useFlashActions(['info', 'error']);

    const order = ref(null);
    const step = ref(0);
    const substep = ref(0);

    const client = ref({});
    const asset = ref({
      meta: {}
    });
    const contract = ref({});
    const formerContract = ref({});

    const clientForm = ref(null);
    const assetForm = ref(null);
    const contractForm = ref(null);
    const formerContractForm = ref(null);

    const savingContract = ref(false);
    const previewRef = ref(null);
    const toSendZaloNotification = ref(true);

    const contractId = computed(() => root.$route.params.id);
    const renew = computed(() => (root.$route.name === 'contract-renewal'));

    const overdueFee = ref(0);
    const overridedOverdueFee = ref(null);
    const renewalDifference = ref(0);
    const transactions = ref([]);

    const allTransactions = ref([]);

    const emitTransactions = (value) => {
      allTransactions.value = value;
    };

    onMounted(async () => {
      if (props.review) {
        step.value = 1;
      }

      if (contractId.value) {
        const c = await LoanFormService.getContractDetail(contractId.value);

        if (renew.value) {
          if (
            (c.status !== CONTRACT_STATUSES.DISBURSED)
            && (c.status !== CONTRACT_STATUSES.OVERDUE)
          ) {
            root.$router.push(applyParams(PATHS.CONTRACT_DETAIL, c.finfluxId));
          }

          formerContract.value = {
            ...c,
            disbursedOn: moment(c.submittedOn, DATE_FORMAT).format(DISPLAY_DATE_FORMAT)
          };

          formerContractForm.value.loadContractInfo({ ...formerContract.value });
        } else {
          if (
            (c.status !== CONTRACT_STATUSES.PENDING)
            && !props.review
          ) {
            root.$router.push(applyParams(PATHS.CONTRACT_DETAIL, c.finfluxId));
          }
        }

        const renewOverride = renew.value ? {
          id: null,
          finfluxId: null,
          disbursedOn: moment().format(DISPLAY_DATE_FORMAT),
          approvedAmount: c.adjustedAmount ? c.approvedAmount - c.adjustedAmount : c.approvedAmount
        } : {};

        contractForm.value.loadContractInfo({
          ...c,
          disbursedOn: moment(c.submittedOn, DATE_FORMAT).format(DISPLAY_DATE_FORMAT),
          ...renewOverride
        });

        const a = await AssetFormService.getAsset(c.assetId);
        assetForm.value.loadAssetInfo(a);

        const cli = await ClientFormService.getClientById(c.clientId);
        clientForm.value.loadClientInfo(cli);
      } else {
        const { orderId } = root.$route.query;

        if (orderId) {
          order.value = await OrderService.getOrderById(orderId);

          if (order.value.contractFinfluxId) {
            root.$router.push(PATHS.CONTRACTS);
          }

          clientForm.value.loadClientInfo({
            firstName: order.value.firstName,
            primaryPhoneNumber: order.value.phone
          });

          assetForm.value.loadAssetInfo({
            description: order.value.assetDescription,
            expectedAmount: order.value.requiredAmount,
            valuatedAmount: order.value.proposedAmount,
            valuatedById: order.value.validator
          });
        }
      }
    });

    const back = () => {
      root.$router.go(-1);
    };

    const showPreview = () => {
      const forms = [
        clientForm.value,
        assetForm.value,
        contractForm.value
      ];

      let valid = true;

      forms.forEach((form) => {
        const v = form.submit();
        valid = valid && v;
      });

      if (valid) {
        step.value = 1;
        substep.value = 0;
      }
    };

    const showInputs = () => {
      step.value = 0;
      substep.value = 0;
    };

    const saveContractAndPrint = async () => {
      if (savingContract.value) return;
      savingContract.value = true;

      try {
        if (substep.value === 0) {
          const promises = [];

          if (!asset.value.id) {
            promises.push(AssetFormService.createAsset(asset.value));
          } else {
            promises.push(AssetFormService.updateAsset(asset.value.id, asset.value));
          }

          if (!client.value.id) {
            promises.push(ClientFormService.createClient(client.value));
          } else {
            promises.push(ClientFormService.updateClient(client.value.id, client.value));
          }

          const [asset_, client_] = await Promise.all(promises);

          clientForm.value.loadClientInfo({
            ...client.value,
            id: client_.id,
            finfluxId: client_.finfluxId
          });

          assetForm.value.loadAssetInfo({
            ...asset.value,
            id: asset_.id
          });

          let contract_;

          if (!contract.value.id) {
            if (renew.value) {
              if (overridedOverdueFee.value || overridedOverdueFee.value === 0) {
                await LoanFormService.overrideOverdueFee(formerContract.value.id, overridedOverdueFee.value);
              }

              let overdue = overdueFee.value - formerContract.value.chargedOverdueFee;
              if (renewalDifference.value < 0) {
                overdue += renewalDifference.value;
              }

              if (overdue > 0) {
                await LoanFormService.chargeOverdueFee(formerContract.value.id, {
                  registeredOn: contract.value.disbursedOn,
                  amount: overdue
                });
              }

              contract_ = await LoanFormService.createContract({
                ...contract.value,
                assetId: asset_.id,
                clientId: client_.id,
                parentId: formerContract.value.id,
                submittedOn: contract.value.disbursedOn,
                overridedMaturesOn: contract.value.overridedMaturesOn
              });

              LoanFormService.addContractNotes(contract_.id, 'Tạo hợp đồng');

              let deductedStorageFee = 0;
              await transactions.value.reduce(async (promise, t) => {
                await promise;

                let { amount } = t;
                const remain = contract.value.storageFee - deductedStorageFee;
                if (remain > 0) {
                  if (amount > remain) {
                    amount -= remain;
                    deductedStorageFee += remain;
                  } else {
                    deductedStorageFee += amount;
                    return Promise.resolve();
                  }
                }

                return LoanFormService.addTransaction(formerContract.value.id, {
                  ...t,
                  amount,
                  action: TRANSACTION_ACTIONS.RENEW,
                  actionOn: contract.value.disbursedOn
                });
              }, Promise.resolve());

              await LoanFormService.renewContract(formerContract.value.finfluxId, {
                renewedOn: contract.value.disbursedOn
              });

              LoanFormService.addContractNotes(formerContract.value.id, 'Gia hạn hợp đồng');
            } else {
              contract_ = await LoanFormService.createContract({
                ...contract.value,
                assetId: asset_.id,
                clientId: client_.id,
                legacyOrderId: order.value?.id,
                submittedOn: contract.value.disbursedOn
              });

              if (order.value) {
                OrderService.updateStage(order.value.id, {
                  stage: ORDER_STAGE.ACTIVE_CONTRACT.val,
                  step: ORDER_STEP.CONTRACT,
                  status: ORDER_STATUS.SUCCESS,
                  contractFinfluxId: contract_.finfluxId
                });
              }

              LoanFormService.addContractNotes(contract_.id, 'Tạo hợp đồng');
            }

            info(root.$t('CREATE_CONTRACT_SUCCESSFULLY'));
          } else {
            contract_ = await LoanFormService.updateContract(contract.value.id, {
              ...contract.value,
              assetId: asset_.id,
              clientId: client_.id,
              submittedOn: contract.value.disbursedOn
            });

            info(root.$t('UPDATE_CONTRACT_SUCCESSFULLY'));
            LoanFormService.addContractNotes(contract_.id, 'Chỉnh sửa hợp đồng');
          }

          contractForm.value.loadContractInfo({
            ...contract.value,
            id: contract_.id,
            finfluxId: contract_.finfluxId,
            disbursedOn: moment(contract_.submittedOn, DATE_FORMAT).format(DISPLAY_DATE_FORMAT)
          });

          substep.value = 1;
        }

        savingContract.value = false;

        setTimeout(() => {
          previewRef.value.print();
        }, 0);
      } catch (err) {
        savingContract.value = false;
        // TODO: prompt correct detailed error message based on error code
        const { message } = err;
        error(message);
      }
    };

    const print = () => {
      setTimeout(() => {
        previewRef.value.print();
      }, 0);
    };

    const toggleToSendZaloNotification = () => {
      toSendZaloNotification.value = !toSendZaloNotification.value;
    };

    const goToContractDetail = () => {
      root.$router.push(applyParams(PATHS.CONTRACT_DETAIL, contract.value.finfluxId));
    };

    const complete = () => {
      if (renew.value && toSendZaloNotification.value) {
        LoanFormService.sendRenewedNotification(contract.value.id);
      }

      goToContractDetail();
    };

    const loadRenewalInfo = (obj) => {
      overdueFee.value = obj.overdueFee;
      overridedOverdueFee.value = obj.overridedOverdueFee;
      renewalDifference.value = obj.renewalDifference;

      transactions.value = obj.transactions.filter((t) => (!t.status && !!t.amount)).sort((a, b) => {
        if (a.registeredOn < b.registeredOn) {
          return -1;
        }

        if (a.registeredOn > b.registeredOn) {
          return 1;
        }

        if (
          (a.type === TRANSACTION_TYPES.CASH)
          && (b.type === TRANSACTION_TYPES.BANK)
        ) {
          return -1;
        }

        if (
          (b.type === TRANSACTION_TYPES.CASH)
          && (a.type === TRANSACTION_TYPES.BANK)
        ) {
          return 1;
        }

        return 0;
      });
    };

    return {
      step,
      substep,
      client,
      asset,
      contract,
      showPreview,
      showInputs,
      clientForm,
      assetForm,
      contractForm,
      saveContractAndPrint,
      faPrint,
      previewRef,
      goToContractDetail,
      complete,
      contractId,
      back,
      renew,
      formerContractForm,
      formerContract,
      print,
      loadRenewalInfo,
      allTransactions,
      emitTransactions,
      toSendZaloNotification,
      toggleToSendZaloNotification
    };
  }
};
</script>

<style lang="scss">
@import "~@/assets/scss/colors";

#contract-editor {
  padding-top: 50px;
  padding-bottom: 50px;

  .checkbox-wrapper {
    margin-top: 7px;
    display: block;
    font-size: 14px;
    margin-right: 7px;

    .label {
      margin-right: 5px;
      vertical-align: top;
    }

    input {
      vertical-align: top;
      margin-top: 5px;
      cursor: pointer;
    }
  }

  .locked {
    pointer-events: none;
  }

  h1 {
    margin-bottom: 20px;
    font-size: 28px;
    margin-left: 20px;
  }

  .box {
    background-color: $white;
    border-radius: 4px;
    margin: 15px;
    padding: 20px 0;
    padding-bottom: 40px;
    max-width: 1200px;

    h2 {
      font-size: 20px;
      font-weight: bold;
      margin-bottom: 30px;
      margin-top: 20px;
    }

    .input-wrapper {
      > .input {
        width: 100%;
        margin-bottom: 20px;
      }
    }
  }

  .controls {
    text-align: right;
    margin-top: 20px;
    max-width: 1200px;

    .primary-button {
      margin-left: 15px;
      border-radius: 4px;
    }
  };

  .preview-controls {
    background-color: $white;
    font-size: 20px;
    padding: 30px;
    position: fixed;
    top: 200px;
    right: 15px;
    width: 350px;
    border-radius: 4px;

    @media (max-width: 1439px) {
      opacity: 0.5;
      transition: all .2s ease-in-out;

      &:hover {
        opacity: 1;
      }
    }

    center {
      font-weight: bolder;
      text-transform: uppercase;
    }

    .buttons {
      margin-top: 28px;

      .button {
        margin: 0px 4px;
        box-shadow: 0px 3px 6px $silver;
      }

      .primary-button {
        padding: 0px 20px;

        /deep/ span {
          font-size: 15px;
        }
      }
    }
  }
}
</style>
